import React, { useState, useEffect } from "react";
import OrganizationDetailsForm from "./OrganizationDetailsForm";
import {
  Button,
  IconButton,
  TextField,
  Box,
  RadioGroup,
  Radio,
  FormControl,
  MenuItem,
  FormLabel,
  FormControlLabel,
  FormHelperText,
  Typography,
  Tooltip,
} from "@mui/material";
import { Image } from "semantic-ui-react";
import UploadIcon from "@mui/icons-material/Upload";
import CloseIcon from "@mui/icons-material/Close";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import Joyride from "react-joyride";

import Modal from "components/shared/Modal";
import LoaderButton from "components/shared/LoaderButton";
import TourOutline from "components/shared/TourOutline";

import { client_mode, client_mode_description } from "constants/client_properties";
import { useTour, useShouldShowTour } from "hooks/useTour";
import { ADD_CLIENT_MODAL_TOUR, DEFAULT_TOUR_STYLES, TOUR_TITLES, CustomTooltip } from "config/tours";

export default function AddOrganizationModal({ open, onClose, onSave, loading = false, isClient = true }) {
  const [organizationName, setOrganizationName] = useState(null);
  const [clientDetails, setClientDetails] = useState({});
  const [clientDetailsDataError, setClientDetailsDataError] = useState({})
  const [themeColor, setThemeColor] = useState("#db2828"); // default for now
  const [logo, setLogo] = useState(null);
  const label = isClient ? "Client" : "Affiliate";
  const [clientMode, setClientMode] = useState(client_mode.project);

  // Tour management
  const {
    isRunning: isTourRunning,
    stepIndex: tourStepIndex,
    completedSteps,
    startTour,
    handleJoyrideCallback,
    goToStep,
  } = useTour(ADD_CLIENT_MODAL_TOUR.id);

  const shouldAutoStartTour = useShouldShowTour(ADD_CLIENT_MODAL_TOUR.id, open && isClient);

  // Auto-start tour on first modal open
  useEffect(() => {
    if (shouldAutoStartTour && open && isClient) {
      // Small delay to ensure modal is rendered
      const timer = setTimeout(() => {
        startTour();
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [shouldAutoStartTour, open, isClient, startTour]);

  useEffect(() => {
    if (!open) {
      setOrganizationName(null);
      setLogo(null);
    }
  }, [open]);

  function handleLogoUpdate(e) {
    const file = e.target.files && e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = function (event) {
        setLogo({
          content: event.target.result,
          extension: file.name.split(".").pop(),
          type: file.type,
        });
      };
      reader.readAsDataURL(file);
    }
  }

  function handleSave() {
    let inputs = {
      name: organizationName,
      themeColor,
      logo,
    };
    if (isClient) {
      inputs.clientMode = clientMode
      inputs = {...inputs, ...clientDetails}
    } 
    onSave && onSave(inputs);
  }

  return (
    <>
      {/* Joyride Tour */}
      <Joyride
        steps={ADD_CLIENT_MODAL_TOUR.steps}
        run={isTourRunning}
        stepIndex={tourStepIndex}
        continuous
        disableScrolling
        callback={handleJoyrideCallback}
        styles={DEFAULT_TOUR_STYLES}
        tooltipComponent={CustomTooltip}
        floaterProps={{
          disableAnimation: true,
        }}
      />

      {/* Tour Outline Sidebar */}
      {isTourRunning && (
        <TourOutline
          steps={ADD_CLIENT_MODAL_TOUR.steps}
          currentStep={tourStepIndex}
          completedSteps={completedSteps}
          onStepClick={goToStep}
          title={TOUR_TITLES[ADD_CLIENT_MODAL_TOUR.id]}
        />
      )}

      <Modal
        title={
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
            <span>{"Add New " + label}</span>
            {isClient && (
              <Tooltip title="Take a guided tour">
                <IconButton
                  onClick={startTour}
                  size="small"
                  sx={{ ml: 1 }}
                >
                  <HelpOutlineIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        }
        open={open}
        content={
          <div>
            <TextField
              className="add-client-name-field"
              fullWidth
              required
              value={organizationName}
              onChange={(e) => setOrganizationName(e.target.value)}
              label={label + " Name"}
              variant="outlined"
              error={organizationName === ""}
              helperText={organizationName === "" ? `${label} name must not be empty.` : ""}
              sx={{ mt: 1 }}
            />
            {isClient && (
              <div className="add-client-optional-fields">
                <OrganizationDetailsForm
                  setOrganizationDetails={setClientDetails}
                  organizationDetails={clientDetails}
                  setOrganizationDetailsError={setClientDetailsDataError}
                  organizationDetailsError={clientDetailsDataError}
                />

                {logo && (
                  <Box sx={{ mt: 2 }}>
                    <IconButton sx={{ float: "right" }} onClick={() => setLogo(null)}>
                      <CloseIcon />
                    </IconButton>
                    <Image src={logo.content} />
                  </Box>
                )}
                {!logo && (
                  <Box sx={{ mt: 2 }}>
                    <label htmlFor="upload-logo-input">
                      <input type="file" hidden id={"upload-logo-input"} onChange={handleLogoUpdate} />
                      <Button startIcon={<UploadIcon />} component="span">
                        Upload Logo
                      </Button>
                    </label>
                  </Box>
                )}
              </div>
            )}

            {!isClient && (
              <>
                {logo && (
                  <Box sx={{ mt: 2 }}>
                    <IconButton sx={{ float: "right" }} onClick={() => setLogo(null)}>
                      <CloseIcon />
                    </IconButton>
                    <Image src={logo.content} />
                  </Box>
                )}
                {!logo && (
                  <Box sx={{ mt: 2 }}>
                    <label htmlFor="upload-logo-input">
                      <input type="file" hidden id={"upload-logo-input"} onChange={handleLogoUpdate} />
                      <Button startIcon={<UploadIcon />} component="span">
                        Upload Logo
                      </Button>
                    </label>
                  </Box>
                )}
              </>
            )}
          </div>
        }
        actions={
          <>
            <Button variant="outlined" onClick={onClose}>
              Cancel
            </Button>
            <LoaderButton loading={loading} disabled={!organizationName || loading || clientDetailsDataError?.invalidorganizationDetailsForm} variant="contained" onClick={handleSave}>
              Save
            </LoaderButton>
          </>
        }
      />
    </>
  );
}
